import React from 'react';

import isEmpty from 'lodash/isEmpty';

import {
  LegacyBox,
  TableCell,
  FiberManualRecordIcon,
  IndeterminateCheckBoxIcon,
  AddBoxIcon,
} from '@gogame/components';
import {
  useCurrencyFormat,
  useNumberFormat,
  usePercentFormat,
} from '@gogame/intl-ts';

import {
  TableCellDefaultBackground,
  TableCellStyled,
  TableRowActivityTypes,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/components';
import { useValidationErrorsByPath } from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/Validation';
import {
  NODE_LEVELS,
  NODE_LEVELS_SINGULAR,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/constants';
import { getMetricTypeById } from 'scenes/MarketingPlanning/utils';

import {
  ItemTitleLinkRouter,
  ItemTitleBox,
  TitleText,
  SubtitleText,
} from './components';

const Item = ({
  to,
  node,
  path,
  current,
  open,
  currentParent,
  last,
  renderIndicator,
  onClickLink,
}) => {
  const { name, activityTypes, metricTypes, depth, nodes, entityId } = node;
  const activityTypesList = Object.keys(activityTypes);

  const [hover, setHover] = React.useState(false);

  const handleMouseEnterItem = React.useCallback(() => {
    setHover(true);
  }, []);

  const handleMouseLeaveItem = React.useCallback(() => {
    setHover(false);
  }, []);

  const currencyFormatter = useCurrencyFormat();
  const percentFormatter = usePercentFormat();
  const numberFormatter = useNumberFormat();

  const errors = useValidationErrorsByPath(path);

  const formatterByType = {
    percent: percentFormatter,
    number: numberFormatter,
  };

  const nodeLevelsWithGrammaticalNumber = React.useMemo(
    () => (entityId === -1 ? NODE_LEVELS : NODE_LEVELS_SINGULAR),
    [nodes]
  );

  const cBudgetEmpty = depth === 5 && isEmpty(nodes);

  return activityTypesList.map((type, i) => {
    const { budget, budgetAllocated, cMetrics } = activityTypes[type];
    const budgetFormatted = currencyFormatter(budget);
    const budgetAllocatedFormatted = currencyFormatter(budgetAllocated);
    const budgetAllocatedError = !isEmpty(
      errors?.activityTypes[type].budgetAllocated
    );

    return (
      <TableRowActivityTypes
        type={type}
        key={i}
        onMouseEnter={handleMouseEnterItem}
        onMouseLeave={handleMouseLeaveItem}
        data-hover={hover}
        data-selected={current}
        label={name}
      >
        {i === 0 ? (
          <TableCellDefaultBackground
            rowSpan={activityTypesList.length}
            data-hover={hover}
            data-selected={current}
            label={`rowActivityCell_${name}`}
          >
            <ItemTitleLinkRouter to={to} onClick={onClickLink}>
              <ItemTitleBox
                align={'center'}
                depth={depth}
                gutter={8}
                singleRow={activityTypesList.length === 1}
              >
                {last ? (
                  <FiberManualRecordIcon
                    sx={{ fontSize: 16 }}
                    color={'action'}
                  />
                ) : open ? (
                  <IndeterminateCheckBoxIcon
                    sx={{ fontSize: 16 }}
                    color={'action'}
                  />
                ) : (
                  <AddBoxIcon sx={{ fontSize: 16 }} color={'action'} />
                )}
                <LegacyBox grow={1} vertical={true}>
                  {(open || current || currentParent) && depth > 1 ? (
                    <SubtitleText>
                      {nodeLevelsWithGrammaticalNumber[depth]}
                    </SubtitleText>
                  ) : null}
                  <TitleText depth={depth} current={open || current}>
                    {name}
                  </TitleText>
                </LegacyBox>
              </ItemTitleBox>
            </ItemTitleLinkRouter>
          </TableCellDefaultBackground>
        ) : null}
        {renderIndicator({
          activityType: type,
          budget,
          budgetAllocated,
        })}
        <TableCellStyled
          label={`budgetAllocated_${i}`}
          align={'right'}
          error={Number(budgetAllocatedError)}
        >
          {cBudgetEmpty ? '-' : budgetAllocatedFormatted}
        </TableCellStyled>
        <TableCell label={`budgetFormatted_${i}`} align={'right'}>
          {budgetFormatted}
        </TableCell>
        {metricTypes.map(({ id, kindId }, i) => {
          const { value } = cMetrics[id];
          const formattedValue =
            formatterByType[getMetricTypeById(kindId)](value);
          return (
            <TableCell label={`metricTypes_${i}`} key={i} align={'right'}>
              {formattedValue}
            </TableCell>
          );
        })}
      </TableRowActivityTypes>
    );
  });
};

export default Item;
